import { gql } from '@apollo/client'
import React from 'react'

import { generalClient, minicrudsClient } from '../../../../utils/apollo/api'
import { useModalData } from '../../../../components/modals/ModalsContext'
import { useQuery } from '../../../../utils/apollo/_helpers/useQuery'
import { usePromiseQuery } from '../../../../utils/apollo/_helpers/promiseQuery'
import StatePresenter from '../../../../packages/kiti/components/helpers/StatePresenter'

const Context = React.createContext([])
const { Provider } = Context
export const useLinkTreeMenuState = () => React.useContext(Context)
export const useLinkTreeMenu = () => useLinkTreeMenuState()?.linkTree

const QUERY = gql`
  query LinkTree($id: ID) {
    linkTree(id: $id) {
      id
      name
      placeTypes
      placeLevelIds
      assetTypeIds
      enabled
      placeLevels {
        id
        name
      }
      linkTreeItems {
        id
        label
      }
      updatedAt
      insertedAt
    }
  }
`

const ASSET_TYPE_QUERY = gql`
  query AssetType($id: ID) {
    minicrud(id: $id, filter: { includeDeleted: true }) {
      id
      name
    }
  }
`

// The backend only returns raw asset type ids on `linkTree.assetTypeIds` — unlike
// `placeLevelIds`, it has no resolved `assetTypes` join to hydrate them into objects. MinicrudSelect
// needs {id, name} objects to render the previously saved selection, so we fetch them here.
const useHydratedAssetTypes = linkTree => {
  const getAssetType = usePromiseQuery(ASSET_TYPE_QUERY, { client: minicrudsClient })
  const [assetTypes, setAssetTypes] = React.useState(null)
  const assetTypeIds = linkTree?.assetTypeIds
  const key = linkTree ? (assetTypeIds || []).join(',') : null

  React.useEffect(() => {
    if (!linkTree) return undefined
    if (!assetTypeIds?.length) {
      setAssetTypes([])
      return undefined
    }

    let cancelled = false
    Promise.all(assetTypeIds.map(assetTypeId => getAssetType({ id: assetTypeId }))).then(results => {
      if (!cancelled) setAssetTypes(results.map(result => result.data?.minicrud).filter(Boolean))
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return assetTypes
}

const LinkTreeMenuLoader = ({ children }) => {
  const { id } = useModalData()
  const { data, ...state } = useQuery(QUERY, {
    skip: !id,
    variables: { id },
    client: generalClient,
    fetchPolicy: 'no-cache'
  })

  const assetTypes = useHydratedAssetTypes(data?.linkTree)
  const loadingAssetTypes = !!data?.linkTree && assetTypes === null

  const value = data?.linkTree ? { ...data, linkTree: { ...data.linkTree, assetTypeIds: assetTypes || [] } } : data

  return (
    <StatePresenter {...state} loading={state.loading || loadingAssetTypes}>
      <Provider value={value}>{children}</Provider>
    </StatePresenter>
  )
}

export default LinkTreeMenuLoader
